import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ClasificadorGastoPartida, ExpenseClassificationService } from '../../../../../modules/expense/expenses/expense-classification.service';
import { CompanyModeService } from '../../../../../services/company-mode.service';
import { GrantService } from '../../../../income/grant/services/grant.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ActualSelected, LocalDB } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { ConboxDialogComponent } from '../../../../../shared/components/add-file/dialog-conbox.component';
import { ContactFormV2Component } from '../../../../contacts/components/contact-form-v2/contact-form-v2.component';
import { ContactModel } from '../../../../../modules/contact/contact/contact.model';
import {
  INVESTMENT_ASSET_EMPTY_GUID,
  INVESTMENT_ASSET_KINDS,
  INVESTMENT_ASSET_TYPE_INVESTMENT,
  InvestmentAssetKind,
  InvestmentAssetRecord,
  InvestmentAssetTax,
  InvestmentAssetType,
  roundMoney,
} from '../../models/investment-asset.model';
import { InvestmentAssetEntityService } from '../../services/investment-asset-entity.service';
import { InvestmentAssetReadService } from '../../services/investment-asset-read.service';
import { InvestmentAssetLookupsFacade, InvestmentAssetOption } from '../../services/investment-asset-lookups.facade';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { MasterService } from '../../../../master/services/master.service';

export interface InvestmentAssetFormDialogData {
  type: InvestmentAssetType;
  uid?: string;
}

type FormErrors = Partial<Record<'account' | 'provider' | 'dateBuy' | 'dateStartWorking' | 'maximumLinealCoeficient' | 'maximumPeriod' | 'totalWithoutTax', string>>;

interface TaxOption {
  id: string;
  display: string;
  group: string;
  tax: InvestmentAssetTax;
}

@Component({
  selector: 'app-investment-asset-form-dialog',
  templateUrl: './investment-asset-form-dialog.component.html',
  styleUrls: ['./investment-asset-form-dialog.component.scss'],
  standalone: false,
})
export class InvestmentAssetFormDialogComponent implements OnInit, OnDestroy {
  kind: InvestmentAssetKind;
  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: FormErrors = {};

  accounts: InvestmentAssetOption[] = [];
  contacts: InvestmentAssetOption[] = [];
  taxOptions: TaxOption[] = [];

  readonly amortizationTypeControl = new UntypedFormControl({ value: INVESTMENT_ASSET_TYPE_INVESTMENT, disabled: true });

  loadingCoefficient = false;
  coefficientNotFound = false;
  loadingContacts = false;

  loadingFile = false;
  fileUid: string | null = null;
  isImage = false;
  isPdf = false;
  imageUrl: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  accountConfig: any;
  providerConfig: any;
  taxesConfig: any = { label: 'Impuestos', placeholder: 'Buscar impuesto…', options: [], groupBy: 'group' };

  isProjectEnable = false;
  projects: InvestmentAssetOption[] = [];

  isNonprofit = false;
  clasificadorCatalog: ClasificadorGastoPartida[] = [];
  grantOptions: { id: string; display: string }[] = [];

  subpartidasOf(): any[] {
    const partida = this.clasificadorCatalog.find(p => p.nombre === this.form.get('clasificadorPartida').value);
    return partida ? partida.subpartidas : [];
  }

  familiasOf(): string[] {
    const subpartida = this.subpartidasOf().find(s => s.nombre === this.form.get('clasificadorSubpartida').value);
    return subpartida ? subpartida.familias : [];
  }

  onPartidaChange(): void {
    this.form.patchValue({ clasificadorSubpartida: null, clasificadorFamilia: null });
  }

  onSubpartidaChange(): void {
    this.form.patchValue({ clasificadorFamilia: null });
  }

  private loadGrants(projectUid: string | null): Promise<void> {
    this.grantOptions = [];
    if (!projectUid) return Promise.resolve();
    return firstValueFrom(this.grantService.getBySearch({ page: 1, pageSize: 200, projectUid, kind: 'all' }))
      .then(response => {
        const values = (response && Array.isArray(response.values)) ? response.values : [];
        this.grantOptions = values.map((g: any) => ({ id: g.uid, display: g.provider || g.elementDescription || g.uid }));
      })
      .catch(() => { this.grantOptions = []; });
  }

  private wireNonprofitFields(): void {
    this.form.get('project').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(project => {
      this.form.get('grant').setValue(null, { emitEvent: false });
      this.loadGrants(project && project.id ? String(project.id) : null);
    });
  }

  private applyNonprofitRecord(record: any): void {
    this.form.patchValue({
      clasificadorPartida: record.partida || null,
      clasificadorSubpartida: record.subpartida || null,
      clasificadorFamilia: record.familia || null,
    }, { emitEvent: false });
    this.loadGrants(record.projectUid || null).then(() => {
      this.form.get('grant').setValue(record.grantUid ? (this.grantOptions.find(g => String(g.id).toLowerCase() === String(record.grantUid).toLowerCase()) || null) : null, { emitEvent: false });
    });
  }

  private nonprofitPayload(): any {
    if (!this.isNonprofit) return {};
    const value = this.form.getRawValue();
    return {
      partida: value.clasificadorPartida || null,
      subpartida: value.clasificadorSubpartida || null,
      familia: value.clasificadorFamilia || null,
      grantUid: value.grant && value.grant.id ? value.grant.id : null,
    };
  }

  private loaded: InvestmentAssetRecord | null = null;
  private objectUrl: string | null = null;
  private coefficientSeq = 0;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: InvestmentAssetFormDialogData): Observable<boolean> {
    return dialog.open<InvestmentAssetFormDialogComponent, InvestmentAssetFormDialogData, boolean>(InvestmentAssetFormDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<InvestmentAssetFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: InvestmentAssetFormDialogData,
    private entity: InvestmentAssetEntityService,
    private reads: InvestmentAssetReadService,
    private lookups: InvestmentAssetLookupsFacade,
    private addFileService: AddFileService,
    private sanitizer: DomSanitizer,
    private notify: NotificationService,
    private activityService: ActivityService,
    private masterService: MasterService,
    private grantService: GrantService,
    private companyMode: CompanyModeService,
    private classificationService: ExpenseClassificationService,
  ) {
    this.kind = INVESTMENT_ASSET_KINDS[data && data.type] || INVESTMENT_ASSET_KINDS[1];
    this.form = this.fb.group({
      account: [null],
      elementDescription: [''],
      provider: [null],
      dateBuy: [null],
      dateStartWorking: [null],
      maximumLinealCoeficient: [null],
      maximumPeriod: [null],
      totalWithoutTax: [null],
      taxes: [[]],
      project: [null],
      clasificadorPartida: [null],
      clasificadorSubpartida: [null],
      clasificadorFamilia: [null],
      grant: [null],
    });
    this.wireNonprofitFields();
  }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar inversión' : 'Nueva inversión';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return [this.loaded.elementDescription, this.loaded.provider].filter(Boolean).join(' · ') || this.kind.title;
    }
    return 'Registra un elemento de ' + this.kind.singular;
  }

  get accountControl(): UntypedFormControl {
    return this.form.get('account') as UntypedFormControl;
  }

  get providerControl(): UntypedFormControl {
    return this.form.get('provider') as UntypedFormControl;
  }

  get elementDescriptionControl(): UntypedFormControl {
    return this.form.get('elementDescription') as UntypedFormControl;
  }

  get dateBuyControl(): UntypedFormControl {
    return this.form.get('dateBuy') as UntypedFormControl;
  }

  get dateStartWorkingControl(): UntypedFormControl {
    return this.form.get('dateStartWorking') as UntypedFormControl;
  }

  get maximumLinealCoeficientControl(): UntypedFormControl {
    return this.form.get('maximumLinealCoeficient') as UntypedFormControl;
  }

  get maximumPeriodControl(): UntypedFormControl {
    return this.form.get('maximumPeriod') as UntypedFormControl;
  }

  get totalWithoutTaxControl(): UntypedFormControl {
    return this.form.get('totalWithoutTax') as UntypedFormControl;
  }

  get taxesControl(): UntypedFormControl {
    return this.form.get('taxes') as UntypedFormControl;
  }

  get hasFile(): boolean {
    return !!this.fileUid && this.fileUid !== INVESTMENT_ASSET_EMPTY_GUID;
  }

  get selectedTaxes(): TaxOption[] {
    return (this.form.get('taxes').value || []) as TaxOption[];
  }

  get totalTax(): number {
    const base = Number(this.form.get('totalWithoutTax').value) || 0;
    return roundMoney(this.selectedTaxes.reduce((acc, option) => acc + (Number(option.tax.taxImport) || 0) / 100 * base, 0));
  }

  get total(): number {
    return roundMoney((Number(this.form.get('totalWithoutTax').value) || 0) + this.totalTax);
  }

  ngOnInit(): void {
    this.accountConfig = {
      label: 'Cuenta de inversión',
      placeholder: 'Buscar cuenta…',
      options: this.accounts,
      autoSelectSingle: true,
      strict: true,
      onChange: (option: any) => this.onAccountChange(option),
    };
    this.providerConfig = {
      label: 'Proveedor',
      placeholder: 'Buscar o escribir proveedor…',
      options: this.contacts,
      allowCustomValues: true,
      customValueLabel: 'Usar este proveedor',
    };

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.dateBuyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value) this.form.get('dateStartWorking').setValue(value);
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  newContact(): void {
    const scratch = new ContactModel();
    scratch.isProvider = true;
    scratch.isClient = false;
    scratch.isPhysical = true;
    const typed = this.providerControl.value;
    if (typed && typeof typed === 'object' && typed.display && !this.findContact(typed.id)) {
      scratch.companyName = typed.display;
    }

    ContactFormV2Component.open(this.dialog, { scratch, dockLeft: true }).subscribe(result => {
      if (!result) return;
      this.loadingContacts = true;
      this.lookups.contacts()
        .then(list => {
          this.setContacts(list);
          const created = this.contacts.find(option => (result.uid && String(option.id).toLowerCase() === String(result.uid).toLowerCase())
            || (result.companyName && option.display === result.companyName));
          if (created) this.providerControl.setValue(created);
        })
        .catch(() => this.notify.error('No se pudo actualizar el listado de contactos'))
        .then(() => { this.loadingContacts = false; });
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    const pdf = file.type === 'application/pdf' || name.endsWith('.pdf');
    const image = /\.(png|jpe?g)$/.test(name) || file.type === 'image/png' || file.type === 'image/jpeg';
    if (!pdf && !image) {
      this.notify.warning('Solo se admiten archivos PDF, PNG o JPG.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    this.loadingFile = true;
    this.addFileService.attachFile(formData, consts.INVESTMENT_ENTITY)
      .then(response => {
        if (!response || response.error) {
          this.notify.error(response && response.message ? response.message : 'No se pudo subir el archivo.');
          return;
        }
        LocalDB.RemoveForFiles();
        this.fileUid = response.uid;
        this.isPdf = pdf;
        this.isImage = !pdf;
        this.showPreview(file);
      })
      .catch(error => this.notify.error(error && error.error && error.error.message ? error.error.message : 'No se pudo subir el archivo.'))
      .finally(() => { this.loadingFile = false; });
  }

  pickFromFiles(): void {
    this.dialog.open(ConboxDialogComponent, {
      width: '80%',
      height: '80%',
      maxWidth: '96vw',
      data: { originEntity: consts.INVESTMENT_ENTITY, filePath: consts.INVESTMENT_FILE_PATH }
    }).afterClosed().subscribe(result => {
      if (!result || !result.uid) return;
      const pdf = result.type === consts.TYPE_FILE_PDF;
      const image = result.type === consts.TYPE_FILE_IMAGE;
      if (!pdf && !image) return;

      this.loadingFile = true;
      const request = pdf
        ? this.addFileService.getImageFromFilesPDF(result.uid, consts.INVESTMENT_ENTITY)
        : this.addFileService.getImageFromFiles(result.uid, consts.INVESTMENT_ENTITY);
      request
        .then(blob => {
          this.fileUid = result.uid;
          this.isPdf = pdf;
          this.isImage = image;
          this.showPreview(blob);
        })
        .catch(() => this.notify.error('No se pudo cargar el documento seleccionado.'))
        .finally(() => { this.loadingFile = false; });
    });
  }

  removeFile(): void {
    this.fileUid = null;
    this.isImage = false;
    this.isPdf = false;
    this.revokePreview();
  }

  save(): void {
    if (this.saving || this.loadingFile) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const editing = this.isEdit;
    const request = editing ? this.entity.update(this.data.uid, payload) : this.entity.create(payload);

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(editing ? 'Inversión actualizada correctamente' : 'Inversión creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message) || (editing
          ? 'Ha ocurrido un error inesperado al actualizar la inversión'
          : 'Ha ocurrido un error inesperado al crear la inversión');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<InvestmentAssetRecord | null> = this.isEdit ? this.entity.get(this.data.uid) : Promise.resolve(null);

    Promise.all([record, this.lookups.form(this.kind.type), this.activityService.loadData(ActualSelected.activitySelected)])
      .then(([investment, lookups, activity]) => {
        this.setAccounts(lookups.accounts);
        this.setContacts(lookups.contacts);
        this.setTaxOptions(lookups.taxes, investment ? investment.taxes : []);
        this.loaded = investment;
        this.isProjectEnable = !!(activity && activity.isProject);

        const projectsPromise = this.isProjectEnable
          ? firstValueFrom(this.masterService.getProjects())
          : Promise.resolve<InvestmentAssetOption[]>([]);

        return Promise.all([projectsPromise, this.companyMode.isNonProfit().catch(() => false), this.classificationService.getCatalog()]).then(([projects, isNonprofit, catalog]) => {
          this.projects = projects || [];
          this.isNonprofit = !!isNonprofit;
          this.clasificadorCatalog = catalog || [];
          if (investment) {
            this.applyRecord(investment);
          } else {
            this.form.patchValue({ dateBuy: new Date(), dateStartWorking: new Date() }, { emitEvent: false });
          }
          this.ready = true;
        });
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(investment: InvestmentAssetRecord): void {
    const accountNumber = investment.accountActivity || '';
    let account = this.accounts.find(option => option.id === accountNumber) || null;
    if (!account && accountNumber) {
      account = { id: accountNumber, display: accountNumber };
      this.setAccounts([account, ...this.accounts]);
    }

    const contact = this.findContact(investment.contactsUid);
    const provider = contact || (investment.provider ? { id: investment.provider, display: investment.provider } : null);

    this.form.patchValue({
      account,
      elementDescription: investment.elementDescription || '',
      provider,
      dateBuy: this.fromApiDate(investment.dateBuy),
      dateStartWorking: this.fromApiDate(investment.dateStartWorking),
      maximumLinealCoeficient: this.numberOrNull(investment.maximumLinealCoeficient),
      maximumPeriod: this.numberOrNull(investment.maximumPeriod),
      totalWithoutTax: this.numberOrNull(investment.totalWithoutTax),
      taxes: this.matchTaxes(investment.taxes || []),
      project: this.projects.find(option => option.id === investment.projectUid) || null,
    }, { emitEvent: false });
    this.applyNonprofitRecord(investment);

    this.fileUid = investment.fileUid && investment.fileUid !== INVESTMENT_ASSET_EMPTY_GUID ? investment.fileUid : null;
    this.isImage = !!investment.image;
    this.isPdf = !!investment.pdf;
    if (this.hasFile && (this.isImage || this.isPdf)) {
      this.loadingFile = true;
      this.addFileService.getImageFromFiles(this.fileUid, consts.INVESTMENT_ENTITY)
        .then(blob => this.showPreview(blob))
        .catch(() => this.notify.warning('No se pudo cargar la vista previa del documento.'))
        .finally(() => { this.loadingFile = false; });
    }
  }

  private onAccountChange(option: any): void {
    const number = option && typeof option === 'object' ? String(option.id || '') : '';
    this.coefficientNotFound = false;
    if (!number) return;
    const seq = ++this.coefficientSeq;
    this.loadingCoefficient = true;
    this.reads.coefficient(number.substring(0, 3))
      .then(result => {
        if (seq !== this.coefficientSeq) return;
        const coefficient = result ? Number(result.maximumLinearCoefficient) || 0 : 0;
        const period = result ? Number(result.maximumPeriodYears) || 0 : 0;
        this.coefficientNotFound = !coefficient && !period;
        this.form.patchValue({ maximumLinealCoeficient: coefficient, maximumPeriod: period });
      })
      .catch(() => {
        if (seq !== this.coefficientSeq) return;
        this.notify.error('No se pudo consultar la tabla de amortización de la cuenta');
      })
      .then(() => { if (seq === this.coefficientSeq) this.loadingCoefficient = false; });
  }

  private validate(): FormErrors {
    const value = this.form.getRawValue();
    const errors: FormErrors = {};
    const account = value.account && typeof value.account === 'object' ? String(value.account.id || '').trim() : '';
    const provider = value.provider && typeof value.provider === 'object' ? String(value.provider.display || '').trim() : String(value.provider || '').trim();

    if (!account) errors.account = 'Selecciona la cuenta de inversión';
    else if (!this.accounts.some(option => option.id === account)) errors.account = 'Selecciona una cuenta de la lista';
    if (!provider) errors.provider = 'Indica el proveedor';
    if (!value.dateBuy) errors.dateBuy = 'Indica la fecha de compra';
    if (!value.dateStartWorking) errors.dateStartWorking = 'Indica la fecha de puesta en funcionamiento';
    if (!(Number(value.maximumLinealCoeficient) > 0)) errors.maximumLinealCoeficient = 'El coeficiente lineal máximo debe ser mayor que 0';
    if (!(Number(value.maximumPeriod) > 0)) errors.maximumPeriod = 'El periodo máximo debe ser mayor que 0';
    if (!(Number(value.totalWithoutTax) > 0)) errors.totalWithoutTax = 'El total sin impuestos debe ser mayor que 0';

    return errors;
  }

  private buildPayload(): InvestmentAssetRecord {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const providerValue = value.provider;
    const contact = providerValue && typeof providerValue === 'object' ? this.findContact(providerValue.id) : null;
    const provider = providerValue && typeof providerValue === 'object' ? String(providerValue.display || '').trim() : String(providerValue || '').trim();

    const payload: InvestmentAssetRecord = {
      ...base,
      accountActivity: String(value.account.id).trim(),
      elementDescription: (value.elementDescription || '').toString().trim(),
      provider,
      contactsUid: contact ? contact.id : INVESTMENT_ASSET_EMPTY_GUID,
      dateBuy: formatDate(value.dateBuy, 'yyyy-MM-dd', 'es-ES'),
      dateStartWorking: formatDate(value.dateStartWorking, 'yyyy-MM-dd', 'es-ES'),
      typeInvestment: INVESTMENT_ASSET_TYPE_INVESTMENT,
      maximumLinealCoeficient: Number(value.maximumLinealCoeficient),
      maximumPeriod: Number(value.maximumPeriod),
      totalWithoutTax: roundMoney(Number(value.totalWithoutTax)),
      taxes: this.selectedTaxes.map(option => ({ ...option.tax })),
      image: this.hasFile && this.isImage,
      pdf: this.hasFile && this.isPdf,
      fileUid: this.hasFile ? this.fileUid as string : INVESTMENT_ASSET_EMPTY_GUID,
      manualMovement: null,
      projectUid: this.isProjectEnable && value.project && value.project.id ? value.project.id : null,
      ...this.nonprofitPayload(),
    };

    if (!this.isEdit) {
      delete (payload as any).uid;
      payload.fiscalYear = parseInt(ActualSelected.yearSelected, 10) || new Date().getFullYear();
      payload.activityUid = ActualSelected.activitySelected;
      payload.lifetime = payload.lifetime || '';
      payload.residualValue = payload.residualValue || 0;
    }

    return payload;
  }

  private setAccounts(list: InvestmentAssetOption[]): void {
    this.accounts = list || [];
    this.accountConfig = { ...this.accountConfig, options: this.accounts };
  }

  private setContacts(list: InvestmentAssetOption[]): void {
    this.contacts = list || [];
    this.providerConfig = { ...this.providerConfig, options: this.contacts };
  }

  private setTaxOptions(masterTaxes: any[], recordTaxes: InvestmentAssetTax[]): void {
    const options: TaxOption[] = (masterTaxes || []).map(tax => {
      const meta = tax.metadata || {};
      return {
        id: String(tax.id),
        display: tax.display || meta.name,
        group: meta.type || 'Otros',
        tax: { uid: String(tax.id), taxId: Number(meta.id) || 0, name: meta.name || tax.display, taxImport: Number(meta.import) || 0, type: meta.type || '' },
      };
    });

    (recordTaxes || []).forEach(tax => {
      if (this.findTaxOption(options, tax)) return;
      options.push({
        id: 'record_' + tax.taxId + '_' + options.length,
        display: tax.name,
        group: tax.type || 'Otros',
        tax: { uid: tax.uid, taxId: tax.taxId, name: tax.name, taxImport: tax.taxImport, type: tax.type },
      });
    });

    this.taxOptions = options;
    this.taxesConfig = { ...this.taxesConfig, options };
  }

  private matchTaxes(taxes: InvestmentAssetTax[]): TaxOption[] {
    const selected: TaxOption[] = [];
    taxes.forEach(tax => {
      const option = this.findTaxOption(this.taxOptions, tax, selected.map(item => item.id));
      if (option && !selected.some(item => item.id === option.id)) selected.push(option);
    });
    return selected;
  }

  private findTaxOption(options: TaxOption[], tax: InvestmentAssetTax, exclude: string[] = []): TaxOption | null {
    const available = options.filter(option => exclude.indexOf(option.id) === -1);
    const byUid = tax.uid ? available.find(option => String(option.tax.uid).toLowerCase() === String(tax.uid).toLowerCase()) : null;
    if (byUid) return byUid;
    const byTaxId = available.find(option => option.tax.taxId && option.tax.taxId === tax.taxId);
    if (byTaxId) return byTaxId;
    const byName = available.find(option => option.tax.name === tax.name || option.display === tax.name);
    if (byName) return byName;
    return available.find(option => option.tax.type === tax.type && Number(option.tax.taxImport) === Number(tax.taxImport)) || null;
  }

  private findContact(id: any): InvestmentAssetOption | null {
    if (!id || id === INVESTMENT_ASSET_EMPTY_GUID) return null;
    const key = String(id).toLowerCase();
    return this.contacts.find(option => String(option.id).toLowerCase() === key) || null;
  }

  private showPreview(blob: Blob): void {
    this.revokePreview();
    if (!blob) return;
    this.objectUrl = URL.createObjectURL(blob);
    if (this.isPdf) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } else {
      this.imageUrl = this.objectUrl;
    }
  }

  private revokePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.imageUrl = null;
    this.pdfUrl = null;
  }

  private fromApiDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) || date.getFullYear() <= 1 ? null : date;
  }

  private numberOrNull(value: any): number | null {
    return value === null || value === undefined || value === '' || isNaN(Number(value)) ? null : Number(value);
  }
}
